import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getChhaVisitNoteForm,
  updateChhaVisitNoteForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  BrainIcon,
  CalendarIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HomeIcon,
  NotesIcon,
  PillIcon,
  ShieldCheckIcon,
  UsersIcon,
  UtensilsIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  CHHA_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  AMBULATION_OPTIONS,
  ASSISTIVE_DEVICE_OPTIONS,
  CHHA_VISIT_ARRAY_KEYS,
  EDUCATION_RESPONSE_OPTIONS,
  HOME_HAZARD_OPTIONS,
  MENTAL_STATUS_OPTIONS,
  NUTRITION_ASSISTANCE_OPTIONS,
  PERSONAL_CARE_OPTIONS,
  SAFETY_MEASURE_OPTIONS,
  SKIN_OBSERVATION_OPTIONS,
  TRANSFER_ASSISTANCE_OPTIONS,
  emptyChhaVisitPayload,
  today,
  toggleInList,
} from "./chhaVisitNoteContent";
import ChhaNotesCommonHeader from "./ChhaNotesCommonHeader";
import {
  normalizeChhaCommonHeaderState,
  resolveChhaNotesCommonHeader,
  withNormalizedChhaCommonHeader,
} from "./chhaNotesCommonHeaderUtils";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "../nurseNotes/nurseVisitCommon";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const toBool = (value) => value === true || value === "true" || value === 1;

const emptyValues = () => ({
  ...emptyChhaVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const ChhaVisitNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyChhaVisitPayload();
      const loadedPayload =
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickNurseVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizeChhaCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        remindedPatientMeds: toBool(loadedPayload.remindedPatientMeds),
        reportedSkinConcernToRn: toBool(loadedPayload.reportedSkinConcernToRn),
        communicatedToSupervisingRn: toBool(
          loadedPayload.communicatedToSupervisingRn,
        ),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      CHHA_VISIT_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : [];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveChhaNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName || draft?.formName || "CHHA Visit Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyChhaVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getChhaVisitNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveChhaNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        CHHA_FORM_EDITOR_ROLES,
        "View only — only HHA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType: form?.formType || draft?.formType || clinicalForm?.formType,
      role,
      canEdit,
      preferredStaffId: draft?.visit?.staffId ?? null,
      currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
    });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...clinicalFields
    } = values;

    const nestedPayload = withNormalizedChhaCommonHeader({
      ...clinicalFields,
      remindedPatientMeds: Boolean(values.remindedPatientMeds),
      reportedSkinConcernToRn: Boolean(values.reportedSkinConcernToRn),
      communicatedToSupervisingRn: Boolean(values.communicatedToSupervisingRn),
    });

    CHHA_VISIT_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateChhaVisitNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            CHHA Visit Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health aide clinical documentation for personal care visits.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth || form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Visit Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Visit Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(e) => setValue("visitDate", e.target.value)}
                  max={VISIT_DATE_MAX}
                  compact
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <ChhaNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={CalendarIcon} title="7. Visit Information">
          <Field label="Supervising RN">
            <input
              type="text"
              className={fieldClass}
              value={values.supervisingRn || ""}
              disabled={!canEdit}
              placeholder="e.g. Sarah Jenkins, RN"
              onChange={(e) => setValue("supervisingRn", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={BrainIcon} title="8. Patient Condition Upon Arrival">
          <SubHeading>Mental Status</SubHeading>
          <div className="mt-1.5">
            <ExclusiveChoiceGrid
              name="mentalStatus"
              options={MENTAL_STATUS_OPTIONS}
              value={values.mentalStatus || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("mentalStatus", opt)}
              columns={4}
            />
          </div>
          <div className="mt-3">
            <Field label="Mood / Observations">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.moodObservations || ""}
                disabled={!canEdit}
                placeholder="Describe patient's mood and any initial physical observations…"
                onChange={(e) => setValue("moodObservations", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={HeartPulseIcon} title="9. Vital Signs (Optional)">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2">
            <Field label="BP (Sitting)">
              <input
                type="text"
                className={fieldClass}
                value={values.bpSitting || ""}
                disabled={!canEdit}
                placeholder="120/80"
                onChange={(e) => setValue("bpSitting", e.target.value)}
              />
            </Field>
            <Field label="Pulse (BPM)">
              <input
                type="text"
                className={fieldClass}
                value={values.pulseBpm || ""}
                disabled={!canEdit}
                placeholder="72"
                onChange={(e) => setValue("pulseBpm", e.target.value)}
              />
            </Field>
            <Field label="Temp (°F)">
              <input
                type="text"
                className={fieldClass}
                value={values.tempF || ""}
                disabled={!canEdit}
                placeholder="98.6"
                onChange={(e) => setValue("tempF", e.target.value)}
              />
            </Field>
            <Field label="Resp (BPM)">
              <input
                type="text"
                className={fieldClass}
                value={values.respBpm || ""}
                disabled={!canEdit}
                placeholder="16"
                onChange={(e) => setValue("respBpm", e.target.value)}
              />
            </Field>
            <Field label="Weight (lbs)">
              <input
                type="text"
                className={fieldClass}
                value={values.weightLbs || ""}
                disabled={!canEdit}
                placeholder="165.4"
                onChange={(e) => setValue("weightLbs", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={UsersIcon} title="10. Personal Care Provided">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <CheckboxGrid
                options={PERSONAL_CARE_OPTIONS}
                values={values.personalCare}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("personalCare", opt)}
                columns={1}
              />
            </div>
            <Field label="Specific Details / Care Notes">
              <textarea
                rows={6}
                className={textareaClass}
                value={values.personalCareNotes || ""}
                disabled={!canEdit}
                placeholder="Detail any refusal of care or specific techniques used…"
                onChange={(e) => setValue("personalCareNotes", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section icon={HomeIcon} title="11. Mobility & Transfers">
            <div className="space-y-2">
              <Field label="Ambulation / Bed Mobility">
                <select
                  className={fieldClass}
                  value={values.ambulationLevel || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("ambulationLevel", e.target.value)}
                >
                  <option value="">Select…</option>
                  {AMBULATION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Transfer Assistance Level">
                <select
                  className={fieldClass}
                  value={values.transferAssistance || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("transferAssistance", e.target.value)
                  }
                >
                  <option value="">Select…</option>
                  {TRANSFER_ASSISTANCE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              <div>
                <SubHeading>Assistive Device Used</SubHeading>
                <CheckboxGrid
                  options={ASSISTIVE_DEVICE_OPTIONS}
                  values={values.assistiveDevices}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("assistiveDevices", opt)}
                  columns={2}
                />
              </div>
            </div>
          </Section>

          <Section icon={UtensilsIcon} title="12. Nutritional Support">
            <div className="space-y-2">
              <Field label="Meal Prep / Feeding">
                <textarea
                  rows={2}
                  className={textareaClass}
                  value={values.mealPrepFeeding || ""}
                  disabled={!canEdit}
                  placeholder="What was served?"
                  onChange={(e) => setValue("mealPrepFeeding", e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Meal Completion (%)">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.mealCompletionPercent || ""}
                    disabled={!canEdit}
                    placeholder="e.g. 75%"
                    onChange={(e) =>
                      setValue("mealCompletionPercent", e.target.value)
                    }
                  />
                </Field>
                <Field label="Fluid Intake (oz/ml)">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.fluidIntake || ""}
                    disabled={!canEdit}
                    placeholder="16 oz"
                    onChange={(e) => setValue("fluidIntake", e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Assistance Level">
                <select
                  className={fieldClass}
                  value={values.nutritionAssistance || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("nutritionAssistance", e.target.value)
                  }
                >
                  <option value="">Select…</option>
                  {NUTRITION_ASSISTANCE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </Section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section
            icon={PillIcon}
            title="13. Medication Reminders"
            className="border-l-[3px] border-l-red-500"
          >
            <div className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] text-red-800 flex gap-2">
              <AlertCircleIcon size={14} className="shrink-0 mt-0.5" />
              <p>
                <span className="font-semibold">Important policy:</span> CHHA
                staff must NOT administer medications. Only verbal/visual
                reminders are permitted.
              </p>
            </div>
            <label className="mt-2 inline-flex items-center gap-2 text-[12px] font-medium text-slate-700">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600"
                checked={Boolean(values.remindedPatientMeds)}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("remindedPatientMeds", e.target.checked)
                }
              />
              Reminded patient to take meds
            </label>
            <div className="mt-2">
              <Field label="Notes / Refusals">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.medicationReminderNotes || ""}
                  disabled={!canEdit}
                  placeholder="Note any specific reminders or refusals…"
                  onChange={(e) =>
                    setValue("medicationReminderNotes", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>

          <Section icon={HeartPulseIcon} title="14. Skin Observation">
            <CheckboxGrid
              options={SKIN_OBSERVATION_OPTIONS}
              values={values.skinObservations}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("skinObservations", opt)}
              columns={2}
            />
            <div className="mt-2">
              <Field label="Location / Status Details">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.skinLocationDetails || ""}
                  disabled={!canEdit}
                  placeholder="Describe any areas of concern…"
                  onChange={(e) =>
                    setValue("skinLocationDetails", e.target.value)
                  }
                />
              </Field>
            </div>
            <label className="mt-2 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-[12px] font-medium text-slate-700">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600"
                checked={Boolean(values.reportedSkinConcernToRn)}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("reportedSkinConcernToRn", e.target.checked)
                }
              />
              Reported concern to RN
            </label>
          </Section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section icon={ShieldCheckIcon} title="15. Safety Assessment">
            <Field label="Home Environment Hazards">
              <select
                className={fieldClass}
                value={values.homeEnvironmentHazards || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("homeEnvironmentHazards", e.target.value)
                }
              >
                <option value="">Select…</option>
                {HOME_HAZARD_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <div className="mt-2">
              <SubHeading>Safety Measures Taken</SubHeading>
              <CheckboxGrid
                options={SAFETY_MEASURE_OPTIONS}
                values={values.safetyMeasuresTaken}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("safetyMeasuresTaken", opt)}
                columns={1}
              />
            </div>
          </Section>

          <Section icon={NotesIcon} title="16. Patient / Caregiver Education">
            <Field label="Education Topic">
              <input
                type="text"
                className={fieldClass}
                value={values.educationTopic || ""}
                disabled={!canEdit}
                placeholder="e.g. Infection Control / Handwashing"
                onChange={(e) => setValue("educationTopic", e.target.value)}
              />
            </Field>
            <div className="mt-2">
              <SubHeading>Response / Understanding</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="educationResponse"
                  options={EDUCATION_RESPONSE_OPTIONS}
                  value={values.educationResponse || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("educationResponse", opt)}
                  columns={2}
                />
              </div>
            </div>
          </Section>
        </div>

        <Section icon={FileCheckIcon} title="17. Status Changes & Response">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Field label="Changes in Condition">
                <textarea
                  rows={4}
                  className={textareaClass}
                  value={values.changesInCondition || ""}
                  disabled={!canEdit}
                  placeholder="Note any new symptoms, decline in function, or specific complaints…"
                  onChange={(e) =>
                    setValue("changesInCondition", e.target.value)
                  }
                />
              </Field>
              <label className="inline-flex items-center gap-2 text-[12px] font-medium text-slate-700">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600"
                  checked={Boolean(values.communicatedToSupervisingRn)}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("communicatedToSupervisingRn", e.target.checked)
                  }
                />
                Communicated to Supervising RN
              </label>
            </div>
            <Field label="General Response / Additional Notes">
              <textarea
                rows={5}
                className={textareaClass}
                value={values.generalResponseNotes || ""}
                disabled={!canEdit}
                placeholder="How did the patient feel after the visit? Any pending needs for next visit?"
                onChange={(e) =>
                  setValue("generalResponseNotes", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
          clinicianSectionTitle="Authentication & Certification"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

ChhaVisitNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default ChhaVisitNoteForm;
